import { Box, CircularProgress, styled } from '@mui/material';
import { PLOTS_PER_PAGE } from 'utils/pagination.utils';
import { InfoTooltip, ThemeTypography } from 'DesignSystem';
import React, {
  FC,
  ForwardedRef,
  ReactNode,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react';
import { Booleanish, Tbooleanish } from 'types/booleanish.types';
import { EAvailableSizes } from 'DesignSystem/types/size.types';
import { IPlot } from 'FeatureModules/Site/types/plot.types';
import FarmPlots from './FarmPlots';
import FlexBox from 'Components/FlexBox';
import PageSubTitle from 'Components/PageSubTitle';
import { IGeoDataIssue } from 'FeatureModules/DataImport/types/dataset.types';

type IPaginatedMapProps = {
  paginationRef?: ForwardedRef<{ resetPagination: () => void }>;
  plots: IPlot[];
  TitleSummary?: ReactNode;
  hasNextPage: boolean;
  itemCount: number;
  /**
   * In edit mode the edit button is shown in the info popup
   * @default edit
   */
  mode?: 'edit' | 'view';
  /**
   * Warnings that are shown in the info popup
   */
  issues?: IGeoDataIssue[];
  /**
   * Default is 600px
   */
  minHeight?: number;
  loading?: boolean;
  itemsPerPage?: number;
  title?: string;
  hideTitle?: boolean;
  tooltipText?: string;
  /**
   * If the view should only show the neighbouring farm plots of a specific dataset
   */
  geoDataId?: string;
  onLoadMorePlots: () => void;
};

const LinkButton = styled('div')<{ loading?: Tbooleanish; disabled?: Tbooleanish }>(
  ({ theme, loading, disabled }) => ({
    display: 'flex',
    alignItems: 'center',
    gap: 4,
    fontSize: 12,
    color:
      loading === 'true' || disabled === 'true'
        ? theme.palette.text.disabled
        : theme.custom.themeColors.black,
    cursor: loading === 'true' || disabled === 'true' ? 'cursor' : 'pointer',
    textDecoration: 'underline',
    pointerEvents: loading === 'true' || disabled === 'true' ? 'none' : 'auto',
  })
);

const PaginatedMap: FC<IPaginatedMapProps> = ({
  plots,
  hasNextPage,
  loading,
  mode = 'edit',
  itemsPerPage = PLOTS_PER_PAGE,
  itemCount,
  issues,
  TitleSummary,
  geoDataId,
  minHeight,
  title,
  paginationRef,
  hideTitle,
  tooltipText = 'Overview all origin cultivation areas for this partner. This could be locations of sites that this partner own or locations that they source from.',
  onLoadMorePlots,
}) => {
  const [currentPage, setCurrentPage] = useState<number>(0);
  const [lastPage, setLastPage] = useState<number | null>(null);

  const currentPlots: IPlot[] = useMemo(
    () => plots.slice(currentPage * itemsPerPage, (currentPage + 1) * itemsPerPage),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [plots, currentPage]
  );

  const hasNextPageAvailable = useMemo(
    () => hasNextPage || currentPage < Math.ceil(itemCount / itemsPerPage) - 1,
    [itemCount, itemsPerPage, hasNextPage, currentPage]
  );

  useEffect(() => {
    setLastPage(prev =>
      hasNextPage && (!prev || (prev && currentPage > prev)) ? currentPage + 1 : prev
    );

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasNextPage, currentPage]);

  const resetPagination = useCallback(() => {
    setCurrentPage(0);
    setLastPage(null);
  }, [setCurrentPage, setLastPage]);

  const handleLoadNextPlots = async () => {
    if (plots.length <= (currentPage + 1) * itemsPerPage) {
      await onLoadMorePlots();
    }
    setCurrentPage(prev => prev + 1);
  };

  const handleLoadPreviousPlots = () => {
    if (currentPage > 0) {
      setCurrentPage(prev => prev - 1);
    }
  };

  const handleFarmPlotsUpdated = () => resetPagination();

  const startCultivatedAreaIndex = useMemo(
    () => currentPage * itemsPerPage + 1,
    [currentPage, itemsPerPage]
  );

  const endCultivatedAreaIndex = useMemo(
    () => Math.min(itemsPerPage + currentPage * itemsPerPage, itemCount),
    [currentPage, itemCount, itemsPerPage]
  );

  useImperativeHandle(
    paginationRef,
    () => ({
      resetPagination,
    }),
    [resetPagination]
  );

  return (
    <Box flex={1} minHeight={400} height="100%">
      <PageSubTitle
        title={hideTitle ? '' : title || 'Origin cultivation areas'}
        spaceBetween={false}
        mb={1}
        sx={{ textWrap: 'nowrap' }}
      >
        <FlexBox width="100%" alignItems="center" gap={0.5}>
          {!hideTitle && (
            <FlexBox ml={1}>
              {itemCount > 0 && (
                <>
                  <ThemeTypography variant="BODY_MEDIUM" color="GRAY_100">
                    ({startCultivatedAreaIndex}-{endCultivatedAreaIndex}{' '}
                    {itemCount ? `of ${itemCount}` : undefined})
                  </ThemeTypography>
                  <InfoTooltip text={tooltipText} size={EAvailableSizes.SMALL} />
                </>
              )}
            </FlexBox>
          )}

          <FlexBox gap={1}>
            {hideTitle && (
              <FlexBox gap={0.5}>
                <ThemeTypography variant="BODY_MEDIUM" color="GRAY_100">
                  Showing {plots.length ? 'plots' : 'farms'} {startCultivatedAreaIndex}-
                  {endCultivatedAreaIndex} {itemCount ? `of ${itemCount}` : undefined}
                </ThemeTypography>
                <InfoTooltip text={tooltipText} size={EAvailableSizes.SMALL} />
              </FlexBox>
            )}

            {!!lastPage && currentPage > 0 && (
              <LinkButton
                onClick={loading ? undefined : handleLoadPreviousPlots}
                disabled={Booleanish(loading)}
              >
                Load previous
              </LinkButton>
            )}
            {(loading || (!!itemCount && itemCount > itemsPerPage && hasNextPageAvailable)) && (
              <LinkButton
                loading={Booleanish(loading)}
                onClick={loading ? undefined : handleLoadNextPlots}
              >
                Load next
                {loading && <CircularProgress sx={{ marginLeft: 1 }} color="primary" size={12} />}
              </LinkButton>
            )}
          </FlexBox>

          {TitleSummary}
        </FlexBox>
      </PageSubTitle>

      {/* 38px from the SubTitle header  */}
      <Box height="calc(100% - 38px)" width="100%">
        <FarmPlots
          mode={mode}
          geoDataId={geoDataId}
          minHeight={minHeight}
          plots={currentPlots}
          issues={issues}
          onFarmPlotsUpdated={handleFarmPlotsUpdated}
        />
      </Box>
    </Box>
  );
};

export default PaginatedMap;
